import React, { useCallback, useEffect, useState } from 'react';
import { FlaskConical, Loader2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { bapi, fmtDateTime, fmtMoney, startHostedPayment } from '@/lib/billingApi';
import { StatusPill } from '@/components/billing/StatusPill';
import { RefundDialog } from './RefundDialog';
import { TestTag, statusText } from './shared';

const TestRow = ({ p, onRefund }) => (
  <li className="flex flex-wrap items-center justify-between gap-2 py-2" data-testid={`test-charge-row-${p.id}`}>
    <div className="min-w-0 text-sm">
      <span className="font-mono text-slate-700">{p.order_id}</span>{p.simulated && <TestTag />}
      <span className="block text-xs text-slate-500">{fmtDateTime(p.created_at)} · {fmtMoney(p.total, p.currency)}{p.refunded_amount ? ` · refunded ${fmtMoney(p.refunded_amount, p.currency)}` : ''}</span>
    </div>
    <div className="flex items-center gap-2">
      <StatusPill status={p.status} label={statusText(p.status)} testId={`test-charge-status-${p.id}`} />
      {['paid', 'partially_refunded'].includes(p.status) && (
        <Button size="sm" variant="outline" onClick={() => onRefund(p)} data-testid={`test-charge-refund-${p.id}`}><Undo2 className="w-3.5 h-3.5 mr-1" />Refund</Button>
      )}
    </div>
  </li>
);

// Charge $1 USD to the admin's own card on the live hosted page, then refund it here.
export const TestChargeCard = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refundP, setRefundP] = useState(null);
  const load = useCallback(() => bapi('/admin/billing/transactions', { query: { kind: 'test', limit: 5 } })
    .then((d) => setRows(d.rows)).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try { await startHostedPayment('/admin/billing/test-charge', {}); } catch (e) { toast.error(e.message); setBusy(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="test-charge-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2"><FlaskConical className="w-5 h-5 text-[#00247D]" />Live $1 USD test charge</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-600">
          Pay $1.00 USD with your own card on the real Plug'n Pay page to confirm the gateway charges in USD. It works even while card checkout is paused,
          grants no plan, sends no receipt and is left out of revenue. Refund it below afterwards.
        </p>
        <Button onClick={run} disabled={busy} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="test-charge-run-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-1.5" />}Run $1 USD test charge
        </Button>
        {rows?.length > 0 && (
          <ul className="divide-y divide-slate-100 border-t border-slate-100" data-testid="test-charge-list">
            {rows.map((p) => <TestRow key={p.id} p={p} onRefund={setRefundP} />)}
          </ul>
        )}
        {refundP && <RefundDialog open p={refundP} onClose={() => setRefundP(null)} onDone={() => { setRefundP(null); load(); }} />}
      </CardContent>
    </Card>
  );
};
